import { Box, Divider, Paper } from "@mui/material";
import type {
	CellClassParams,
	ColDef,
	ColGroupDef,
	ITooltipParams,
	RowClassParams,
	RowStyle,
	ValueFormatterParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { type ReactNode, useCallback, useMemo } from "react";
import { mesfColors, mesfTheme } from "@/setup/ag-grid/mesf-theme";
import {
	type AccountSummaryFigures,
	buildWorksheetRows,
	EMPTY,
	formatAmount,
	formatQuantity,
	isRedFigure,
	POOL_COLUMNS,
	type WorksheetField,
	type WorksheetRow,
} from "../models/SummaryByCloseDate";

const HEADER_HEIGHT = 30;
const GROUP_HEADER_HEIGHT = 28;
const ROW_HEIGHT = 34;

const RIGHT_CELL = "ag-right-aligned-cell";
const RIGHT_HEADER = "ag-right-aligned-header";
const CENTER_HEADER = "ag-cell-center-header";
// The project's own class for the hairline that closes a column group. Applied to the
// last column of each pool so the three of them read as blocks.
const GROUP_SEPARATOR = "ag-column-group-separator";
const LINE_CELL = "account-summary-line-cell";
const POOL_HEADER = "account-summary-pool-header";
const RED_FIGURE = "account-summary-red-figure";
const REGULAR_LABEL = "account-summary-regular-label";

// Eight rows and no scrolling, so the sheet can breathe: a step up from the report
// default in font size and row height.
const worksheetTheme = mesfTheme.withParams({
	fontSize: 13,
	headerFontSize: 12,
});

/**
 * A subtotal closes a block and the ending inventory closes the sheet, so each gets a
 * rule above it and a tint, the way the legacy draws its lines. The darker rule on the
 * ending row is what tells the two apart at a glance. Both are bold, as on the RollShop
 * print, except the ending row's label.
 */
const SUBTOTAL_ROW: RowStyle = {
	backgroundColor: mesfColors.grey[100],
	fontWeight: "700",
	borderTop: `2px solid ${mesfColors.grey[300]}`,
};

const TOTAL_ROW: RowStyle = {
	backgroundColor: mesfColors.grey[100],
	fontWeight: "700",
	borderTop: `2px solid ${mesfColors.text.primary}`,
};

const rowHelp = (params: ITooltipParams<WorksheetRow>) =>
	params.data?.help ?? "";

/**
 * A deduction is printed inside parentheses rather than with a minus sign, which is
 * how the sheet has always marked one. Which cells are deductions belongs to the row,
 * not to the column: the same field is a deduction under one pool and a receipt under
 * another.
 */
const inParentheses = (
	params: ValueFormatterParams<WorksheetRow, number | null>,
	text: string,
): string => {
	if (!text) return EMPTY;
	const field = params.colDef.field as WorksheetField | undefined;
	return field && params.data?.deductions.includes(field) ? `(${text})` : text;
};

const quantityCell = (
	params: ValueFormatterParams<WorksheetRow, number | null>,
) => inParentheses(params, formatQuantity(params.value));

const amountCell = (
	params: ValueFormatterParams<WorksheetRow, number | null>,
) => inParentheses(params, formatAmount(params.value));

const redFigureRule = (field: WorksheetField) => ({
	[RED_FIGURE]: (params: CellClassParams<WorksheetRow, number | null>) =>
		isRedFigure(params.data, field, params.value),
});

const COLUMN_DEFS: (ColDef<WorksheetRow> | ColGroupDef<WorksheetRow>)[] = [
	{
		// No header: the first column names the line, and an accounting sheet does not
		// title that column. It spans both header rows on its own.
		field: "label",
		headerName: "",
		flex: 2,
		minWidth: 230,
		cellClass: [LINE_CELL, GROUP_SEPARATOR],
		cellClassRules: {
			[REGULAR_LABEL]: (params) => params.data?.kind === "total",
		},
		headerClass: GROUP_SEPARATOR,
		// The house grid theme wraps text with `white-space: normal !important`, so a
		// long label cannot be clipped. Let the row grow instead of spilling over the
		// row below.
		autoHeight: true,
		wrapText: true,
		tooltipValueGetter: rowHelp,
	},
	...POOL_COLUMNS.map(
		({
			headerName,
			help,
			quantityField,
			amountField,
		}): ColGroupDef<WorksheetRow> => ({
			headerName,
			headerTooltip: help,
			headerClass: [CENTER_HEADER, GROUP_SEPARATOR, POOL_HEADER],
			// Quantity and Amount describe the same pool; dragging one out of its group
			// would leave a figure with no unit.
			marryChildren: true,
			children: [
				{
					field: quantityField,
					headerName: "Quantity",
					headerTooltip: `How many rolls, in ${headerName}`,
					flex: 0.8,
					minWidth: 90,
					cellClass: RIGHT_CELL,
					cellClassRules: redFigureRule(quantityField),
					headerClass: RIGHT_HEADER,
					valueFormatter: quantityCell,
					tooltipValueGetter: rowHelp,
				},
				{
					field: amountField,
					headerName: "Amount (USD)",
					headerTooltip: `US dollars, in ${headerName}`,
					flex: 1.2,
					minWidth: 120,
					cellClass: [RIGHT_CELL, GROUP_SEPARATOR],
					cellClassRules: redFigureRule(amountField),
					headerClass: [RIGHT_HEADER, GROUP_SEPARATOR],
					valueFormatter: amountCell,
					tooltipValueGetter: rowHelp,
				},
			],
		}),
	),
];

interface Props {
	summary: AccountSummaryFigures;
	header: ReactNode;
}

export const AccountSummaryGrid = ({ summary, header }: Props) => {
	const rows = useMemo(() => buildWorksheetRows(summary), [summary]);

	const getRowStyle = useCallback(
		(params: RowClassParams<WorksheetRow>): RowStyle | undefined => {
			if (params.data?.kind === "subtotal") return SUBTOTAL_ROW;
			if (params.data?.kind === "total") return TOTAL_ROW;
			return undefined;
		},
		[],
	);

	return (
		<Paper
			elevation={3}
			sx={{
				flex: 1,
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
				// The grid theme centres every cell and strips its padding, both with
				// !important, so the right alignment every figure needs has to be reclaimed
				// here rather than through cellStyle.
				"& .ag-right-aligned-cell": {
					justifyContent: "flex-end !important",
					textAlign: "right !important",
					paddingRight: "16px !important",
					fontVariantNumeric: "tabular-nums",
				},
				// AG Grid right-aligns a header by reversing the label's flex direction, so
				// flex-end on its own pushes the text back against the left edge.
				"& .ag-right-aligned-header .ag-header-cell-label": {
					flexDirection: "row !important",
					justifyContent: "flex-end !important",
					paddingRight: "16px !important",
				},
				[`& .${LINE_CELL}`]: {
					justifyContent: "flex-start !important",
					textAlign: "left !important",
					paddingLeft: "16px !important",
				},
				[`& .${RED_FIGURE}`]: {
					color: `${mesfColors.error.dark} !important`,
				},
				[`& .${REGULAR_LABEL}`]: {
					fontWeight: "400 !important",
				},
				// One rule between pools, through both header rows and the body, so the three
				// blocks stand apart instead of running into each other.
				[[
					"& .ag-header-cell.ag-column-group-separator",
					"& .ag-header-group-cell.ag-column-group-separator",
					"& .ag-cell.ag-column-group-separator",
				].join(", ")]: {
					borderRight: `1px solid ${mesfColors.grey[300]} !important`,
				},
				// The pool is the heading and Quantity and Amount are its subtitles, so the
				// two header rows have to read as two levels rather than as one repeated.
				"& .ag-header-group-cell-label": {
					fontSize: "0.8rem",
					fontWeight: 600,
					color: mesfColors.text.primary,
				},
				// The pool name is centred over its pair and both subtitles hang from the
				// right edge of their own column, so nothing in that block lines up by
				// itself. The tint and the rule under the name give the group a span the eye
				// can follow down to Quantity and Amount.
				[`& .ag-header-group-cell.${POOL_HEADER}`]: {
					backgroundColor: mesfColors.grey[50],
					borderBottom: `2px solid ${mesfColors.grey[300]} !important`,
				},
			}}
		>
			{header}
			<Divider />
			<Box sx={{ flex: 1, minHeight: 0 }}>
				<AgGridReact<WorksheetRow>
					theme={worksheetTheme}
					rowData={rows}
					columnDefs={COLUMN_DEFS}
					getRowStyle={getRowStyle}
					// The sheet is a roll forward: every line is defined by the one above it,
					// so it is read in order and never sorted.
					defaultColDef={{ resizable: true, sortable: false }}
					headerHeight={HEADER_HEIGHT}
					groupHeaderHeight={GROUP_HEADER_HEIGHT}
					rowHeight={ROW_HEIGHT}
					enableBrowserTooltips
					suppressContextMenu
					animateRows={false}
				/>
			</Box>
		</Paper>
	);
};
